class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 50);

    const facetForm = this.querySelector('form');
    if (!facetForm) return;

    const sortOnlyFormIds = ['FacetSortDrawerForm', 'FacetSortForm', 'FacetSortDrawerFormMobile'];
    const isSortOnlyForm = sortOnlyFormIds.includes(facetForm.id);
    const isAjaxFacetForm =
      facetForm.id === 'FacetFiltersForm' ||
      (typeof facetForm.id === 'string' && facetForm.id.startsWith('FacetFiltersFormMobile-'));

    const submitSortPreservingFilters = () => {
      const params = new URLSearchParams(window.location.search);
      const formData = new FormData(facetForm);
      for (const [key, value] of formData) {
        if (value) params.set(key, value);
      }
      window.location.href = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    };

    // Checkbox/radio: only `change` debounces — both `input`+`change` would reset the timer and add ~1× wait twice.
    facetForm.addEventListener('input', (e) => {
      if (isSortOnlyForm && e.target.name === 'sort_by') {
        submitSortPreservingFilters();
      } else if (!e.target.matches?.('input[type="checkbox"], input[type="radio"]')) {
        this.debouncedOnSubmit(e);
      }
    });
    facetForm.addEventListener('change', (e) => {
      /* Non-AJAX forms sync immediately; AJAX forms sync in renderFilters */
      if (e.target?.matches?.('input[type="checkbox"]') && !isAjaxFacetForm) {
        FacetFiltersForm.syncAllFilterActiveState();
      }
      if (isSortOnlyForm && e.target.name === 'sort_by') {
        submitSortPreservingFilters();
      } else if (isAjaxFacetForm && e.target?.matches?.('input[type="checkbox"]')) {
        this.onSubmitHandler(e);
      } else {
        this.debouncedOnSubmit(e);
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    /*
     * Checkbox focus scrolls the sr-only input into view before `change` fires, so capturing
     * scrollTop in renderFilters is often already "wrong". Capture on pointerdown (capture phase)
     * runs before focus default actions — keeps the user's list position for AJAX restore.
     */
    if (facetForm.classList.contains('mobile-facets')) {
      facetForm.addEventListener(
        'pointerdown',
        (e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          const body = e.target.closest?.('.ns-facets-drawer__body');
          if (!body || !facetForm.contains(body)) return;
          FacetFiltersForm._pointerScrollSnapshot = {
            formId: facetForm.id,
            scrollTop: body.scrollTop,
            at: Date.now(),
          };
        },
        true
      );
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static runFacetsReload(asyncFn) {
    void (async () => {
      try {
        await Promise.resolve(asyncFn());
      } catch (_) {
        FacetFiltersForm.syncAllFilterActiveState();
      }
    })();
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const gridContainer = document.getElementById('ProductGridContainer');
    const collectionEl = gridContainer?.querySelector('.collection');

    const htmlByType = { 'product-grid': null, facets: null };

    const fetchSection = (sectionObj) => {
      const url = `${window.location.pathname}?section_id=${sectionObj.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      const cached = FacetFiltersForm.filterData.find(filterDataUrl);
      return cached ? Promise.resolve(cached.html) : fetch(url).then((r) => r.text()).then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        return html;
      });
    };

    if (!sections.length) return;

    FacetFiltersForm.runFacetsReload(() => {
      const loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );
      loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
      if (collectionEl) collectionEl.classList.add('loading');
      if (countContainer) countContainer.classList.add('loading');
      if (countContainerDesktop) countContainerDesktop.classList.add('loading');

      return Promise.all(sections.map((s) => fetchSection(s).then((html) => ({ type: s.type, html })))).then(
        (results) => {
          results.forEach(({ type, html }) => {
            htmlByType[type] = html;
          });
          const productGridHtml = htmlByType['product-grid'];
          const facetsHtml = htmlByType.facets || productGridHtml;
          if (productGridHtml) {
            FacetFiltersForm.renderProductGridContainer(productGridHtml);
          }
          if (facetsHtml) {
            FacetFiltersForm.renderFilters(facetsHtml, event);
            FacetFiltersForm.renderProductCount(facetsHtml, productGridHtml);
          }
          if (typeof initializeScrollAnimationTrigger === 'function') {
            [productGridHtml, facetsHtml]
              .filter(Boolean)
              .forEach((html) => initializeScrollAnimationTrigger(html.innerHTML));
          }
        }
      );
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    FacetFiltersForm.runFacetsReload(() =>
      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = responseText;
          FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html, html);
          if (typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger(html.innerHTML);
          }
        })
    );
  }

  static renderSectionFromCache(filterDataUrl, event) {
    FacetFiltersForm.runFacetsReload(() => {
      const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html, html);
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(html.innerHTML);
      }
    });
  }

  static renderProductGridContainer(html) {
    const fetchedGrid = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    const domGrid = document.getElementById('ProductGridContainer');
    if (!fetchedGrid || !domGrid) return;
    domGrid.innerHTML = fetchedGrid.innerHTML;
    domGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  /**
   * Snapshot scroll by mobile form id (not element refs) so restore still works after innerHTML
   * swaps and after layout/opacity settle — Dawn-style AJAX facet updates often reset scrollTop.
   */
  static captureMobileDrawerBodyScroll() {
    const out = [];
    document.querySelectorAll('form.mobile-facets .ns-facets-drawer__body').forEach((body) => {
      const form = body.closest('form[id^="FacetFiltersFormMobile-"]');
      if (form?.id) out.push({ formId: form.id, scrollTop: body.scrollTop });
    });
    return out;
  }

  static restoreMobileDrawerBodyScroll(snapshot) {
    if (!snapshot?.length) return;
    snapshot.forEach(({ formId, scrollTop }) => {
      const form = document.getElementById(formId);
      const body = form?.querySelector('.ns-facets-drawer__body');
      if (!body) return;
      const maxScroll = Math.max(0, body.scrollHeight - body.clientHeight);
      body.scrollTop = Math.min(scrollTop, maxScroll);
    });
  }

  /** Prefer scrollTop recorded at pointerdown (before focus/scroll-into-view on the checkbox). */
  static applyPointerScrollSnapshot(snapshot) {
    const ps = FacetFiltersForm._pointerScrollSnapshot;
    if (!ps?.formId || Date.now() - ps.at > 15000) return snapshot;
    const next = snapshot.map((s) => ({ ...s }));
    const idx = next.findIndex((s) => s.formId === ps.formId);
    if (idx >= 0) next[idx] = { ...next[idx], scrollTop: ps.scrollTop };
    else next.push({ formId: ps.formId, scrollTop: ps.scrollTop });
    return next;
  }

  /** Focus inside the scroll body fights scrollTop restore (scroll anchoring / scrollIntoView). */
  static blurIfInsideMobileDrawerBody() {
    const ae = document.activeElement;
    if (!ae || ae === document.body) return;
    if (ae.closest?.('.ns-facets-drawer__body')) ae.blur();
  }

  /** Re-apply after paint, bindEvents, focus, and fade-in — covers Cause 1 (height/scroll reset). */
  static scheduleMobileDrawerScrollRestore(snapshot) {
    if (!snapshot?.length) return;
    const run = () => FacetFiltersForm.restoreMobileDrawerBodyScroll(snapshot);
    run();
    queueMicrotask(run);
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(() => {
        run();
        requestAnimationFrame(run);
      });
    });
    setTimeout(run, 0);
    setTimeout(run, 50);
    setTimeout(run, 120);
  }

  static renderProductCount(facetsHtml, productGridHtml) {
    const parsed = (html) => new DOMParser().parseFromString(html || '', 'text/html');
    const countEl =
      parsed(facetsHtml).getElementById('ProductCount') ||
      parsed(productGridHtml).getElementById('ProductCount') ||
      parsed(facetsHtml).getElementById('ProductCountDesktop') ||
      parsed(productGridHtml).getElementById('ProductCountDesktop');
    if (!countEl) return;
    const count = countEl.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    let mobileDrawerScrollSnapshot = FacetFiltersForm.captureMobileDrawerBodyScroll();
    mobileDrawerScrollSnapshot = FacetFiltersForm.applyPointerScrollSnapshot(mobileDrawerScrollSnapshot);
    FacetFiltersForm._mobileDrawerScrollSnapshot = mobileDrawerScrollSnapshot;
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const mobileFacetFormSelector = 'form[id^="FacetFiltersFormMobile-"]';
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      `#FacetFiltersForm .js-filter, ${mobileFacetFormSelector} .js-filter, #FacetFiltersPillsForm .js-filter`
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      `#FacetFiltersForm .js-filter, ${mobileFacetFormSelector} .js-filter, #FacetFiltersPillsForm .js-filter`
    );

    // Remove facets that are no longer returned from the server.
    // Only remove elements that belong to the SAME section as the fetched HTML —
    // otherwise this wipes out filter items from other drawer instances on the page.
    if (facetDetailsElementsFromFetch.length > 0) {
      const fetchedIds = new Set(Array.from(facetDetailsElementsFromFetch).map((el) => el.id));

      // Extract the section-id suffix from the first fetched element (e.g. "template--12345__breadcrumbs")
      // Shopify section IDs follow the pattern "template--{number}__{section-name}"
      const firstFetchedId = facetDetailsElementsFromFetch[0].id;
      // Shopify section id: template--{digits}__{handle} — strip drawer suffix (-main / -banner) from element ids
      const sectionSuffixMatch = firstFetchedId.match(/template--\d+__[a-z0-9_-]+/i);
      const sectionSuffix = sectionSuffixMatch ? sectionSuffixMatch[0] : null;

      Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
        // If we identified a section suffix, only act on elements from that section
        if (sectionSuffix && !currentElement.id.includes(sectionSuffix)) return;
        if (!fetchedIds.has(currentElement.id)) {
          currentElement.remove();
        }
      });
    }

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const idSelector = '[id="' + CSS.escape(elementToRender.id) + '"]';
      const allWithId = document.querySelectorAll(idSelector);
      // Update every instance of this facet block so all drawers (and desktop) get the same content
      if (allWithId.length > 0) {
        allWithId.forEach((el) => {
          el.innerHTML = elementToRender.innerHTML;
        });
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        const parentId = elementToRender.parentElement?.id;
        if (parentId) {
          const anchor = document.querySelector(
            '#' + CSS.escape(parentId) + ' .js-filter'
          );
          anchor?.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event) {
      const activeJSFilter = event.target.closest('.js-filter');
      const closestJSFilterID = activeJSFilter ? activeJSFilter.id : null;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, activeJSFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, activeJSFilter);

        const newFacetDetailsElement = activeJSFilter;
        const isMobileDrawer = newFacetDetailsElement.classList.contains('mobile-facets__details');
        const searchRoot = isMobileDrawer
          ? newFacetDetailsElement.closest('form')
          : newFacetDetailsElement;
        const newElementSelector = isMobileDrawer
          ? '.mobile-facets__close-button'
          : '.facets__summary';
        const newElementToActivate = searchRoot?.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        /* Cause 2: focus() scrolls focused control into view; skip on mobile drawer (restore handles list position). */
        if (newElementToActivate && !isTextInput && !isMobileDrawer) {
          newElementToActivate.focus();
        }
      }
    }

    FacetFiltersForm.syncAllFilterActiveState();
    FacetFiltersForm.blurIfInsideMobileDrawerBody();
    FacetFiltersForm.scheduleMobileDrawerScrollRestore(mobileDrawerScrollSnapshot);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const domElement = document.querySelector(selector);
      if (!activeFacetsElement || !domElement) return;
      domElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      { source: '.mobile-facets__open', target: '.mobile-facets__open' },
      { source: '.mobile-facets__count', target: '.mobile-facets__count' },
      {
        source: 'form.mobile-facets aside .ns-sidebar__footer.mobile-facets__footer',
        target: 'form.mobile-facets aside .ns-sidebar__footer.mobile-facets__footer'
      },
      { source: '.sorting', target: '.sorting' }
    ];

    mobileElementSelectors.forEach(({ source, target }) => {
      const sourceEl = html.querySelector(source);
      const targetEls = document.querySelectorAll(target);
      if (!sourceEl || targetEls.length === 0) return;
      targetEls.forEach((el) => {
        el.innerHTML = sourceEl.innerHTML;
      });
    });

    document.querySelectorAll('form[id^="FacetFiltersFormMobile-"]').forEach((formMobile) => {
      formMobile.closest('menu-drawer')?.bindEvents?.();
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  /**
   * Sync active state (border/active class) for all filter types in every form instance.
   * Ensures Color, Size, and any other ns-facet filter show the correct border in all mobile drawers and desktop.
   */
  static syncAllFilterActiveState() {
    const mobileForms = document.querySelectorAll('form[id^="FacetFiltersFormMobile-"]');
    const desktopForm = document.getElementById('FacetFiltersForm');
    const forms = [...(desktopForm ? [desktopForm] : []), ...Array.from(mobileForms)];

    forms.forEach((form) => {
      form.querySelectorAll('.ns-facet__boxed-filter, .ns-facet__color-filter').forEach((label) => {
        const input = label.querySelector('input[type="checkbox"]');
        if (!input) return;
        const isChecked = input.checked;
        label.classList.toggle('active', isChecked);

        // Color filter: also toggle li border/rounded (drawer styling)
        if (label.classList.contains('ns-facet__color-filter')) {
          const li = label.closest('li');
          if (li) {
            li.classList.toggle('border', isChecked);
            li.classList.toggle('border-grey-2', isChecked);
            li.classList.toggle('rounded', isChecked);
          }
        }

        // Boxed filter (Size etc.): add !border-black when checked, remove when not. Also swap !border-grey-2
        if (label.classList.contains('ns-facet__boxed-filter')) {
          label.classList.toggle('!border-black', isChecked);
          label.classList.toggle('!border-grey-2', !isChecked);
        }
      });
    });
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const sections = [];
    const productGrid = document.getElementById('product-grid');
    if (productGrid?.dataset?.id) {
      sections.push({ section: productGrid.dataset.id, type: 'product-grid' });
    }
    const facetsSection = document.querySelector('[data-facets-section-id]');
    if (facetsSection?.dataset?.facetsSectionId) {
      sections.push({ section: facetsSection.dataset.facetsSectionId, type: 'facets' });
    }
    if (sections.length) return sections;
    if (productGrid?.dataset?.id) return [{ section: productGrid.dataset.id, type: 'product-grid' }];
    return [];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    const triggerForm = event.target.closest('form');
    const triggerId = triggerForm?.id;
    const sortFormIds = ['FacetSortForm', 'FacetSortDrawerForm', 'FacetSortDrawerFormMobile'];
    const isMobileFacetFormId = (id) => Boolean(id && id.startsWith('FacetFiltersFormMobile-'));

    const appendSortForms = () => {
      sortFilterForms.forEach((form) => {
        if (sortFormIds.includes(form.id)) {
          forms.push(this.createSearchParams(form));
        }
      });
    };

    if (triggerForm && (triggerId === 'FacetFiltersForm' || isMobileFacetFormId(triggerId))) {
      forms.push(this.createSearchParams(triggerForm));
      appendSortForms();
    } else {
      const fallbackFacet =
        document.getElementById('FacetFiltersForm') ||
        document.querySelector('form[id^="FacetFiltersFormMobile-"]');
      if (fallbackFacet) {
        forms.push(this.createSearchParams(fallbackFacet));
        appendSortForms();
      }
    }

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm._pointerScrollSnapshot = null;
FacetFiltersForm._mobileDrawerScrollSnapshot = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
document.addEventListener('DOMContentLoaded', () => FacetFiltersForm.syncAllFilterActiveState());

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
